import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, get } from './api.ts'

export type Page<T> = { items: T[]; nextCursor: string | null }

/** List state with debounced search, keyset paging and in-flight cancellation. */
export function useList<T>(path: string, query: Record<string, string> = {}) {
  const [items, setItems] = useState<T[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abort = useRef<AbortController>()
  const extra = JSON.stringify(query)

  const fetchPage = useCallback(async (search: string, after: string | null, append: boolean) => {
    // Cancel whatever the previous keystroke started, so a slow earlier
    // response cannot land after a newer one and show stale rows.
    abort.current?.abort()
    const ctrl = new AbortController()
    abort.current = ctrl

    append ? setLoadingMore(true) : setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: '25', ...JSON.parse(extra) as Record<string, string> })
      if (search) params.set('q', search)
      if (after) params.set('cursor', after)
      const page = await get<Page<T>>(`${path}?${params}`, ctrl.signal)
      setItems(prev => (append ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
      setError(e instanceof ApiError ? e.message : 'Could not load this list.')
    } finally {
      if (!ctrl.signal.aborted) { setLoading(false); setLoadingMore(false) }
    }
  }, [path, extra])

  // Debounced so typing a name is one request at the end, not one per key.
  // Same delay the pickers use, so a search box and a dropdown feel the
  // same to type into.
  useEffect(() => {
    const t = window.setTimeout(() => void fetchPage(q, null, false), q ? 180 : 0)
    return () => window.clearTimeout(t)
  }, [q, fetchPage])

  useEffect(() => () => abort.current?.abort(), [])

  return {
    items, q, setQ, loading, loadingMore, error,
    hasMore: cursor !== null,
    loadMore: () => { if (cursor) void fetchPage(q, cursor, true) },
    refresh: () => void fetchPage(q, null, false),
    setItems,
  }
}
